import { ReactElement } from 'react'
import { Button, Stack, Typography } from '@mui/material'

interface Props {
  count: number
  onClear: () => void
  /** Noun shown in the count text, e.g. "Wand"/"Wände". Defaults to Element/Elemente. */
  singularLabel?: string
  pluralLabel?: string
  /**
   * When true, render a "no elements selected" row instead of nothing while the selection is empty,
   * so the panel layout stays stable (no jumping). Defaults to hiding when empty.
   */
  showWhenEmpty?: boolean
  dataCy?: string
}

/**
 * Selection summary row shared by the IFC import panels: how many elements are selected plus an
 * "Auswahl aufheben" button. Presentational — the selection source is up to the caller (IFC element
 * selection, wall multi selection, …).
 */
const SelectionInfoRow = ({
  count,
  onClear,
  singularLabel = 'Element',
  pluralLabel = 'Elemente',
  showWhenEmpty = false,
  dataCy,
}: Props): ReactElement | null => {
  if (count === 0 && !showWhenEmpty) return null

  return (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
      <Typography variant="body2">
        {count === 0
          ? `Keine ${pluralLabel} ausgewählt`
          : `${count} ${count === 1 ? singularLabel : pluralLabel} ausgewählt`}
      </Typography>
      <Button variant="text" size="small" onClick={onClear} disabled={count === 0} data-cy={dataCy}>
        Auswahl aufheben
      </Button>
    </Stack>
  )
}

export default SelectionInfoRow
